<script setup lang="ts">
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

type GateManagementReferenceSettingsModel = {
  gateManagementDriverReferenceRequired: boolean;
  gateManagementDriverReferenceValidated: boolean;
  gateManagementUnsetDriversReferenceNumber?: boolean;
};

type ReferenceCollectionMode = 'required' | 'optional' | 'notCollected';

const props = withDefaults(
  defineProps<{
    entitySettings: GateManagementReferenceSettingsModel;
    isGateManagementEnabled: boolean;
    readOnly: boolean;
  }>(),
  {
    readOnly: false
  }
);

const localSettings = computed(() => props.entitySettings);

// The three options are stored as two booleans so warehouses saved before "Not collected"
// existed keep their meaning: Required on is "Required", everything else is "Optional".
const collectionMode = computed<ReferenceCollectionMode>({
  get() {
    if (localSettings.value.gateManagementDriverReferenceRequired) {
      return 'required';
    }
    return localSettings.value.gateManagementUnsetDriversReferenceNumber
      ? 'notCollected'
      : 'optional';
  },
  set(mode) {
    localSettings.value.gateManagementDriverReferenceRequired = mode === 'required';
    localSettings.value.gateManagementUnsetDriversReferenceNumber = mode === 'notCollected';
  }
});

const isRequired = computed(() => collectionMode.value === 'required');

const collectionOptions = computed(() => [
  {
    value: 'required',
    label: t('warehouse.pages.settings.expansionPanels.gateManagement.referenceNumber.required'),
    description: t(
      'warehouse.pages.settings.expansionPanels.gateManagement.referenceNumber.requiredDescription'
    ),
    testId: 'driver-reference-required-radio'
  },
  {
    value: 'optional',
    label: t('warehouse.pages.settings.expansionPanels.gateManagement.referenceNumber.optional'),
    description: t(
      'warehouse.pages.settings.expansionPanels.gateManagement.referenceNumber.optionalDescription'
    ),
    testId: 'driver-reference-optional-radio'
  },
  {
    value: 'notCollected',
    label: t(
      'warehouse.pages.settings.expansionPanels.gateManagement.referenceNumber.notCollected'
    ),
    description: t(
      'warehouse.pages.settings.expansionPanels.gateManagement.referenceNumber.notCollectedDescription'
    ),
    testId: 'driver-reference-not-collected-radio'
  }
]);

watch(
  () => localSettings.value.gateManagementDriverReferenceRequired,
  required => {
    if (!required) {
      localSettings.value.gateManagementDriverReferenceValidated = false;
    }
  }
);
</script>

<template>
  <div v-if="props.isGateManagementEnabled" class="d-flex flex-column my-4">
    <div>
      <h4 class="mb-1">
        {{ t('warehouse.pages.settings.expansionPanels.gateManagement.referenceNumber.title') }}
      </h4>
      <p class="typography-body-md">
        {{
          t('warehouse.pages.settings.expansionPanels.gateManagement.referenceNumber.description')
        }}
      </p>
    </div>

    <v-radio-group
      v-model="collectionMode"
      :disabled="readOnly"
      class="mt-4"
      hide-details
      data-testid="driver-reference-collection-radio-group">
      <div class="d-flex flex-column ga-4">
        <div v-for="option in collectionOptions" :key="option.value">
          <v-radio
            :value="option.value"
            :label="option.label"
            color="primary"
            density="compact"
            :data-testid="option.testId" />
          <p class="typography-body-sm text-medium-emphasis pl-10">
            {{ option.description }}
          </p>
        </div>
      </div>
    </v-radio-group>

    <v-divider class="my-5" />

    <div>
      <p class="typography-body-md mb-1">
        {{
          t(
            'warehouse.pages.settings.expansionPanels.gateManagement.referenceNumber.matchingAppointmentTitle'
          )
        }}
      </p>
      <p class="typography-body-sm">
        {{
          t(
            'warehouse.pages.settings.expansionPanels.gateManagement.referenceNumber.matchingAppointmentDescription'
          )
        }}
      </p>
      <div class="gm-switch-row d-flex align-center pa-1 ga-3">
        <v-switch
          v-model="localSettings.gateManagementDriverReferenceValidated"
          color="primary"
          hide-details
          :disabled="readOnly || !isRequired"
          data-testid="driver-reference-validated-switch" />
        <span :class="isRequired ? 'typography-body-md' : 'typography-body-md text-disabled'">
          {{
            localSettings.gateManagementDriverReferenceValidated
              ? t('warehouse.pages.settings.expansionPanels.gateManagement.referenceNumber.enabled')
              : t(
                  'warehouse.pages.settings.expansionPanels.gateManagement.referenceNumber.disabled'
                )
          }}
        </span>
      </div>
      <p class="typography-body-sm text-medium-emphasis mt-1">
        {{
          t(
            'warehouse.pages.settings.expansionPanels.gateManagement.referenceNumber.matchingAppointmentNote'
          )
        }}
      </p>
    </div>

    <v-divider class="mt-4" />
  </div>
</template>

<style scoped lang="scss">
.gm-switch-row :deep(.v-switch__track) {
  height: 14px;
  min-width: 36px;
  border-radius: 7px;
  opacity: 1;
}

.gm-switch-row :deep(.v-switch__thumb) {
  width: 20px;
  height: 20px;
}

.gm-switch-row :deep(.v-selection-control--checked .v-switch__track) {
  background-color: $color-primary-60;
}

.gm-switch-row :deep(.v-selection-control--checked .v-switch__thumb) {
  background-color: $color-primary-100;
}

.gm-switch-row :deep(:not(.v-selection-control--checked) .v-switch__track) {
  background-color: $color-neutral-40;
}

.gm-switch-row :deep(:not(.v-selection-control--checked) .v-switch__thumb) {
  background-color: $color-background-primary;
}
</style>
